import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { appLinks } from '../../lib/crossSiteLinks';
import { tokens } from '../../components/SEOPageLayout';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';

export default function CiEvidencePage() {
  const faqs = [
    {
      question: 'What is CI evidence?',
      answer: 'CI evidence shows that required automated checks ran and passed before changes were merged, including timestamps and enforcement status.'
    },
    {
      question: 'Why do required checks matter for audits?',
      answer: 'Auditors want proof that testing controls are enforced, not optional. Required checks demonstrate that merges are blocked if tests fail.'
    },
    {
      question: 'What do auditors typically request?',
      answer: 'Evidence for a sampled set of changes including check names, pass/fail results, timestamps, and confirmation that checks were required.'
    },
    {
      question: 'Does evidence collection require write access?',
      answer: 'No. EvidentTrail collects metadata via read-only GitHub access.'
    }
  ];

  return (
    <MarketingLayout
      title="CI Checks and Approval Evidence"
      description="Learn about CI checks as enforcement points for change control. Evidence that testing and validation are enforced, not discretionary."
      h1="CI Checks and Approval Evidence"
      heroText="Continuous Integration (CI) systems are frequently used as enforcement points for change control."
      type="article"
    >
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://evidenttrail.com/' },
          { name: 'CI Evidence', url: 'https://evidenttrail.com/ci-evidence' }
        ]}
      />
      <FaqJsonLd items={faqs} />

      {/* What Auditors Review */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>What Auditors May Review</h2>
            <p className={`${tokens.text.base} mb-6`}>
              Auditors assess whether CI checks provide an effective automated control over code quality and security. The key distinction is between <em>optional</em> checks (informational only) vs. <em>required</em> checks (mandatory enforcement). For SOC 2 CC8.1 (change management) and CC7.1 (testing), auditors will review:
            </p>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>Common CI Check Types</h3>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Unit Tests:</strong> Verify individual components work correctly
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Integration Tests:</strong> Validate system components interact properly
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Linting:</strong> Enforce code style and detect common errors
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">CI Check Status:</strong> Record pass/fail results for required checks
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Build Verification:</strong> Ensure code compiles and packages successfully
                    </span>
                  </li>
                </ul>
              </div>
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>Enforcement Requirements</h3>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Required (not optional):</strong> Checks must block merge if they fail
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Enforced on all PRs:</strong> Cannot be skipped or bypassed
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Results recorded:</strong> Pass/fail status must be auditable
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Sampled changes:</strong> Evidence for 20-30 changes per audit
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <h3 className={`${tokens.heading.h3} mb-4`}>Example: CI Check Evidence for Sampled Change</h3>
            <div className={`${tokens.card} mb-4`}>
              <div className="font-mono text-sm space-y-1 text-slate-300">
                <div><strong className="text-white">PR:</strong> #892 "Add rate limiting to /api/users endpoint"</div>
                <div><strong className="text-white">Required Checks (5):</strong></div>
                <div className="pl-4">✅ test-suite (passed in 2m 34s)</div>
                <div className="pl-4">✅ lint-check (passed in 18s)</div>
                <div className="pl-4">✅ security-scan (passed in 1m 12s)</div>
                <div className="pl-4">✅ build (passed in 45s)</div>
                <div className="pl-4">✅ integration-tests (passed in 3m 5s)</div>
                <div className="mt-2"><strong className="text-white">Enforcement Status:</strong> Required (branch protection enforced)</div>
                <div><strong className="text-white">Merge Allowed:</strong> Yes (all checks passed)</div>
              </div>
            </div>
            <p className={`${tokens.text.small} text-slate-400 italic mb-6`}>
              This evidence demonstrates that automated testing was enforced, not voluntary.
            </p>
          </div>
        </div>
      </section>

      {/* Why CI Evidence Matters */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Why CI Evidence Matters</h2>
            <p className={`${tokens.text.base} mb-6`}>
              CI evidence supports the claim that testing and validation are <em>enforced</em>, not discretionary. This addresses two key audit concerns:
            </p>
            <div className="space-y-6 mb-8">
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>1. Consistency of Control Operation (CC8.1)</h3>
                <p className={tokens.text.base}>
                  Auditors need evidence that controls operated throughout the audit period. If CI checks were optional or frequently bypassed, the control is not considered effective. Continuous CI evidence shows that <strong className="text-white">every change</strong> was subject to automated testing, not just the sampled ones.
                </p>
              </div>
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>2. Reduction of Manual Work (Audit Efficiency)</h3>
                <p className={tokens.text.base}>
                  Organizations that continuously capture CI check results can generate audit evidence packages in <strong className="text-white">minutes instead of hours</strong>. When an auditor requests evidence for 25 sampled changes, you can instantly provide a report showing: PR link, check results (pass/fail), timestamps, and enforcement status for each one.
                </p>
              </div>
            </div>
            <p className={`${tokens.text.base}`}>
              <strong className="text-white">Note:</strong> If your CI checks are <em>optional</em> (informational only), they do not qualify as a control. Auditors will require evidence of other compensating controls, such as manual review checklists. Making checks required via branch protection rules transforms them from "nice to have" to "audit-grade controls."
            </p>
          </div>
        </div>
      </section>

      {/* How EvidentTrail Fits */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>How EvidentTrail Supports CI Evidence</h2>
            <p className={`${tokens.text.base} mb-6`}>
              EvidentTrail records CI check results and enforcement status for each change using <Link to="/read-only-github-audit" className="text-primary-400 hover:underline">read-only GitHub audit access</Link>, providing evidence that required checks were mandatory and passed.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqs.map((item) => (
                <div key={item.question} className={tokens.card}>
                  <h3 className={`${tokens.heading.h3} mb-2`}>{item.question}</h3>
                  <p className={tokens.text.base}>{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Related Topics</h2>
            <div className="grid md:grid-cols-4 gap-6">
              <Link to="/audit-readiness" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h4 className="text-white font-medium mb-2">Audit Readiness</h4>
                <p className="text-sm text-slate-400">Prepare for audit sampling with continuous evidence.</p>
              </Link>
              <Link to="/branch-protection-drift-detection" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h4 className="text-white font-medium mb-2">Branch Protection Drift Detection</h4>
                <p className="text-sm text-slate-400">Detect when required checks are removed from branch protection.</p>
              </Link>
              <Link to="/github-change-control" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h4 className="text-white font-medium mb-2">GitHub Change Control</h4>
                <p className="text-sm text-slate-400">Why auditors prefer ongoing evidence trails.</p>
              </Link>
              <Link to="/github-workflow-drift-detection" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h4 className="text-white font-medium mb-2">Workflow Drift Detection</h4>
                <p className="text-sm text-slate-400">Monitor changes to workflow controls and required checks.</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className={`${tokens.heading.h2} mb-4`}>Learn More About CI Evidence Collection</h2>
            <p className={`${tokens.text.base} mb-8`}>
              See how CI evidence supports audit compliance.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={appLinks.register} className="btn btn-primary text-lg px-8 py-3">
                Get Started
              </a>
              <Link to="/faq" className="text-slate-300 hover:text-white transition-colors">
                Security and audit FAQ →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
